import {Box, Stack, Typography} from "@mui/joy"
import Selectable from "../Selectable"
import {FixtureResponse} from "../../types/fixture"

export type LeagueFixturesProps = {
    fixtures: FixtureResponse[]
    onSelect?: (fixture: FixtureResponse) => void
    /** The fixture being analysed: shown as picked, and no longer worth clicking */
    selectedId?: number
}

const formatKickOff = (value: string) => {
    const date = new Date(value)

    return isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
    })
}

const Crest = ({logo, name}: { logo?: string, name?: string }) =>
    logo
        ? <Box component={"img"} src={logo} alt={name} sx={{width: 20, height: 20, objectFit: "contain", flexShrink: 0}}/>
        : null

/**
 * One match of a competition, as the football data provider lists it.
 *
 * Crests and kick off times come with the fixture, so the row is drawn from a single source; the
 * predictions behind it are only fetched once the row is picked.
 */
export const LeagueFixture = ({fixture, onSelect, selected}: {
    fixture: FixtureResponse
    onSelect?: (fixture: FixtureResponse) => void
    selected?: boolean
}) => {
    const clickable = !!onSelect && !selected

    return <Selectable selected={selected}
                       disabled={selected}
                       onSelect={clickable ? () => onSelect(fixture) : undefined}
                       sx={{
                           cursor: clickable ? "pointer" : "default",
                           ...(selected && {pointerEvents: "none", opacity: 0.75})
                       }}>
        <Stack width={"100%"} alignItems={"center"} direction={"row"} justifyContent={"space-between"} gap={1}>
            <Stack direction={"row"} alignItems={"center"} gap={1} width={"35%"} minWidth={0}>
                <Crest logo={fixture.teams?.home?.logo} name={fixture.teams?.home?.name}/>
                <Typography level={"body-sm"} noWrap>{fixture.teams?.home?.name}</Typography>
            </Stack>

            <Typography level={"body-xs"} noWrap textAlign={"center"}>
                {formatKickOff(fixture.fixture?.date)}
            </Typography>

            <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} gap={1}
                   width={"35%"} minWidth={0}>
                <Typography level={"body-sm"} noWrap>{fixture.teams?.away?.name}</Typography>
                <Crest logo={fixture.teams?.away?.logo} name={fixture.teams?.away?.name}/>
            </Stack>
        </Stack>
    </Selectable>
}

const LeagueFixtures = ({fixtures, onSelect, selectedId}: LeagueFixturesProps) => <Stack gap={1}>
    {fixtures.map(fixture => <LeagueFixture key={fixture.fixture?.id}
                                            fixture={fixture}
                                            onSelect={onSelect}
                                            selected={fixture.fixture?.id === selectedId}/>)}
</Stack>

export default LeagueFixtures
